import Gauge from "../../Components/Gauge";
import LineChart from "../../Components/LineChart";
import DashboardPeriodFilters from "./DashboardPeriodFilters";
import PageHeader from "../../Components/PageHeader";
import { getAllUsers } from "../../Services/User";
import { getDashboardStats } from "../../Services/Dashboard";
import { format_date, getSerialNumber } from "../../Utils/helpers";
import { useQuery } from "react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import "./Dashboard.css";

const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const pad2 = (value) => String(value).padStart(2, "0");

const getDashboardPeriodRange = (year, viewType, month) => {
  const selectedYear = Number(year);

  if (viewType === "monthly") {
    const selectedMonth = Number(month);
    const lastDay = new Date(selectedYear, selectedMonth, 0).getDate();
    return {
      from: `${selectedYear}-${pad2(selectedMonth)}-01`,
      to: `${selectedYear}-${pad2(selectedMonth)}-${pad2(lastDay)}`,
    };
  }

  return {
    from: `${selectedYear}-01-01`,
    to: `${selectedYear}-12-31`,
  };
};

const formatPeriodLabel = (year, viewType, month) =>
  viewType === "monthly"
    ? `${MONTH_NAMES[Number(month) - 1] ?? ""} ${year}`.trim()
    : String(year);

const getLoadingWeightPercent = (loadPercent = 0) => {
  const normalized = Number(loadPercent)?.toFixed(2);
  return normalized ? Number(normalized) / 100 : 0;
};

const formatMoney = (value) =>
  `$${Number(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export default function Dashboard() {
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [viewType, setViewType] = useState("yearly");
  const [month, setMonth] = useState(String(new Date().getMonth() + 1));

  const periodRange = useMemo(
    () => getDashboardPeriodRange(year, viewType, month),
    [year, viewType, month]
  );

  const periodLabel = useMemo(
    () => formatPeriodLabel(year, viewType, month),
    [year, viewType, month]
  );

  const {
    data: statsResponse,
    isFetching: statsFetching,
    isLoading: statsLoading,
  } = useQuery(
    ["getDashboardStats", year, viewType, month],
    () => getDashboardStats({ year, viewType, month }),
    {
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
    }
  );

  const {
    data: usersResponse,
    isFetching: usersFetching,
    isLoading: usersLoading,
  } = useQuery(
    ["dashboard_new_users", periodRange.from, periodRange.to],
    () =>
      getAllUsers(
        1,
        5,
        "user",
        undefined,
        undefined,
        periodRange.from,
        periodRange.to
      ),
    {
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
    }
  );

  const [dashboard, setDashboard] = useState(null);
  const [usersPayload, setUsersPayload] = useState(null);

  useEffect(() => {
    const payload = statsResponse?.data?.data;
    if (!payload) return;

    const matchesPeriod =
      String(payload.year) === String(year) &&
      payload.viewType === viewType &&
      (viewType !== "monthly" || Number(payload.month) === Number(month));

    if (matchesPeriod) {
      setDashboard(payload);
    }
  }, [statsResponse, year, viewType, month]);

  useEffect(() => {
    const payload = usersResponse?.data?.data;
    if (!payload) return;

    setUsersPayload(payload);
  }, [usersResponse, periodRange.from, periodRange.to]);

  const isInitialLoading = statsLoading && !dashboard;
  const isUpdating = statsFetching && !!dashboard;
  const usersInitialLoading = usersLoading && !usersPayload;
  const usersUpdating = usersFetching && !!usersPayload;

  const stats = dashboard?.stats ?? {};
  const chartData = stats?.data ?? [];
  const chartLabels = stats?.labels ?? MONTH_NAMES;

  const chartSummary = useMemo(() => {
    const values = Array.isArray(chartData)
      ? chartData.map((value) => Number(value) || 0)
      : [];
    const total = values.reduce((sum, value) => sum + value, 0);
    let peakLabel = "";
    let peakValue = 0;

    values.forEach((value, index) => {
      if (value >= peakValue) {
        peakValue = value;
        peakLabel = chartLabels[index] ?? "";
      }
    });

    return { total, peakLabel, peakValue };
  }, [chartData, chartLabels]);

  const handleYearChange = useCallback((nextYear) => {
    setYear(nextYear);
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;
    if (Number(nextYear) === currentYear) {
      setMonth((prev) =>
        Number(prev) > currentMonth ? String(currentMonth) : prev
      );
    }
  }, []);

  const fleet = dashboard?.fleet ?? {};
  const ridesStats = dashboard?.ridesStats ?? {};
  const userCount = dashboard?.userCount ?? {};
  const adminEarnings = dashboard?.adminEarnings?.[0] ?? {};
  const shipments = dashboard?.shipments ?? {};
  const userRows = usersPayload?.docs ?? [];

  return (
    <div className="app-content content dashboard">
      <div className="content-wrapper">
        <div className="content-body">
          <section id="configuration">
            <div className="row">
              <div className="col-12">
                <div className="card-content collapse show main-dash-wrapper">
                  <div className="dash-period-bar">
                    <DashboardPeriodFilters
                      viewType={viewType}
                      onViewTypeChange={setViewType}
                      year={year}
                      onYearChange={handleYearChange}
                      month={month}
                      onMonthChange={setMonth}
                    />
                  </div>

                  <PageHeader
                    title="Dashboard"
                    subtitle={`Overview for ${periodLabel}`}
                    className="dashboard-page-header"
                  />

                  <div
                    className={`dash-dashboard-content${
                      isUpdating || usersUpdating ? " is-updating" : ""
                    }`}
                  >
                    {isInitialLoading ? (
                      <div className="dash-dashboard-skeleton" aria-live="polite">
                        <i className="fas fa-circle-notch fa-spin" />
                        <span>Loading dashboard...</span>
                      </div>
                    ) : (
                      <>
                        <div className="dash-section">
                          <div className="custom-dash-grid-1">
                            <div className="dash-stat-card dashCard">
                              <h3 className="dash-stat-card__title">Fleet Status</h3>
                              <div className="dash-stat-card__rows">
                                <div className="dash-stat-card__row">
                                  <p className="totalLabel">Total Fleet</p>
                                  <p className="totalValue">{fleet.totalDrivers ?? 0}</p>
                                </div>
                                <div className="dash-stat-card__row">
                                  <p className="totalLabel">On The Move</p>
                                  <p className="totalValue">{fleet.ongoingShipments ?? 0}</p>
                                </div>
                              </div>
                            </div>

                            <div className="dash-stat-card dashCard dash-stat-card--gauge">
                              <div className="dash-stat-card__gauge-copy">
                                <p className="dash-stat-card__metric">
                                  {ridesStats.averageLoadingWeight
                                    ? `${Number(ridesStats.averageLoadingWeight).toFixed(2)} lbs`
                                    : "0 lbs"}
                                </p>
                                <p className="totalLabel">Avg. Loading Weight</p>
                              </div>
                              <div className="dash-stat-card__gauge">
                                <Gauge
                                  percent={getLoadingWeightPercent(
                                    ridesStats.currentMonthLoadPercent
                                  )}
                                />
                              </div>
                            </div>

                            <div className="dash-stat-card dashCard">
                              <h3 className="dash-stat-card__title">User Status</h3>
                              <div className="dash-stat-card__rows">
                                <div className="dash-stat-card__row">
                                  <p className="totalLabel">Active</p>
                                  <p className="totalValue">{userCount.activeUsers ?? 0}</p>
                                </div>
                                <div className="dash-stat-card__row">
                                  <p className="totalLabel">Inactive</p>
                                  <p className="totalValue">{userCount.inactiveUsers ?? 0}</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="dash-section">
                          <div className="custom-dash-grid-2">
                            <div className="dash-metric-card dashCard">
                              <p className="totalLabel">Revenue</p>
                              <h3 className="dash-metric-card__value">
                                {formatMoney(adminEarnings.revenue)}
                              </h3>
                            </div>

                            <div className="dash-metric-card dashCard">
                              <p className="totalLabel">Profit</p>
                              <h3 className="dash-metric-card__value">
                                {formatMoney(adminEarnings.profit)}
                              </h3>
                            </div>

                            <div className="dash-metric-card dashCard">
                              <p className="totalLabel">Shipments</p>
                              <h3 className="dash-metric-card__value dash-metric-card__value--count">
                                {shipments.completedShipments ?? 0}
                              </h3>
                            </div>
                          </div>
                        </div>

                        <div className="dash-section">
                            <div className="dash-driver-chart">
                              <div className="dash-driver-chart__head">
                                <div className="dash-driver-chart__titles">
                                  <h2 className="dash-driver-chart__title">Driver Registrations</h2>
                                  <p className="dash-driver-chart__meta">
                                    <span>
                                      <strong>{chartSummary.total}</strong> total in {periodLabel}
                                    </span>
                                    {chartSummary.peakValue > 0 && (
                                      <span>
                                        Peak:{" "}
                                        {viewType === "monthly"
                                          ? `Day ${chartSummary.peakLabel}`
                                          : chartSummary.peakLabel}{" "}
                                        ({chartSummary.peakValue})
                                      </span>
                                    )}
                                  </p>
                                </div>
                              </div>
                              <div className="dash-driver-chart__canvas">
                                <LineChart data={chartData} labels={chartLabels} />
                              </div>
                            </div>
                        </div>

                        <div className="dash-section dash-section--table">
                          <h2 className="dash-section__title">
                            New Users
                            <span className="dash-section-period">({periodLabel})</span>
                          </h2>
                          <div className="dash-table-card maain-tabble table-responsive">
                              <div className="table-scroll-inner">
                                <table className="table">
                                  <thead>
                                    <tr>
                                      <th>S No.</th>
                                      <th>User Name</th>
                                      <th>Date Registered</th>
                                      <th>Status</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {usersInitialLoading ? (
                                      <tr>
                                        <td colSpan={4} style={{ textAlign: "center" }}>
                                          <i
                                            className="fas fa-circle-notch fa-spin"
                                            style={{ fontSize: 42 }}
                                          />
                                        </td>
                                      </tr>
                                    ) : userRows.length > 0 ? (
                                      userRows.map((item, index) => (
                                        <tr key={item._id || `${item.email}-${index}`}>
                                          <td>
                                            {getSerialNumber(usersPayload, index)}
                                          </td>
                                          <td>
                                            {item.firstName || (
                                              <span
                                                className="text-muted"
                                                style={{ fontSize: "0.85rem" }}
                                              >
                                                Profile Incomplete
                                              </span>
                                            )}
                                          </td>
                                          <td>
                                            {item?.createdAt
                                              ? format_date(item.createdAt, "DD/MM/YYYY")
                                              : "-"}
                                          </td>
                                          <td>
                                            <span
                                              className={`status-pill ${
                                                !item.firstName || !item.email
                                                  ? "status-pending"
                                                  : item.active
                                                    ? "status-active"
                                                    : "status-inactive"
                                              }`}
                                            >
                                              {!item.firstName || !item.email
                                                ? "Incomplete"
                                                : item.active
                                                  ? "Active"
                                                  : "Inactive"}
                                            </span>
                                          </td>
                                        </tr>
                                      ))
                                    ) : (
                                      <tr>
                                        <td colSpan={4} style={{ textAlign: "center" }}>
                                          No Records Found
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
